<template>
  <form class="customer-site-form" @submit.prevent="handleSubmit">
    <div class="customer-site-form__row">
      <TextInput
        id="customer_site_label"
        :label="$t('customer_accounts.sites.fields.label')"
        v-model="form.label"
        :placeholder="$t('customer_accounts.sites.fields.label_placeholder')"
      />
      <TextInput
        id="customer_site_external_ref"
        :label="$t('customer_accounts.sites.fields.external_ref')"
        v-model="form.external_ref"
        :placeholder="$t('customer_accounts.sites.fields.external_ref_placeholder')"
      />
    </div>

    <AddressInput
      id="customer_site_address"
      :label="$t('customer_accounts.sites.fields.address')"
      v-model="form.address"
      :error="errors.address"
      required
    />

    <TextAreaInput
      id="customer_site_access_notes"
      :label="$t('customer_accounts.sites.fields.access_notes')"
      v-model="form.access_notes"
      :placeholder="$t('customer_accounts.sites.fields.access_notes_placeholder')"
      :rows="2"
    />

    <TextAreaInput
      id="customer_site_notes"
      :label="$t('customer_accounts.sites.fields.notes')"
      v-model="form.notes"
      :rows="2"
    />

    <p v-if="isEdit && usage.open_work_orders" class="customer-site-form__hint">
      <BaseIcon name="info" size="14" />
      {{ $t('customer_accounts.sites.sync_hint', { count: usage.open_work_orders }) }}
    </p>

    <div class="customer-site-form__actions">
      <Btn type="button" variant="outlined" @click="onCancel && onCancel()">
        {{ $t('common.cancel') }}
      </Btn>
      <Btn type="submit" variant="primary" :loading="saving">
        {{ isEdit ? $t('common.save') : $t('customer_accounts.sites.add') }}
      </Btn>
    </div>
  </form>
</template>

<script>
  import { defineComponent, ref, computed, getCurrentInstance } from 'vue';
  import Btn from '@/components/partials/Btn.vue';
  import TextInput from '@/components/inputs/TextInput.vue';
  import TextAreaInput from '@/components/inputs/TextAreaInput.vue';
  import AddressInput from '@/components/inputs/AddressInput.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';

  export default defineComponent({
    name: 'CustomerSiteModal',
    components: { Btn, TextInput, TextAreaInput, AddressInput, BaseIcon },
    props: {
      site: { type: Object, default: null },
      onSubmit: { type: Function, required: true },
      onCancel: { type: Function, default: null },
    },
    setup(props) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;

      const isEdit = computed(() => !!props.site?.id);
      const usage = computed(() => props.site?.usage || {});
      const saving = ref(false);
      const errors = ref({});

      const s = props.site || {};
      const form = ref({
        label: s.label || '',
        external_ref: s.external_ref || '',
        address: { ...(s.address || {}) },
        access_notes: s.access_notes || '',
        notes: s.notes || '',
      });

      const validate = () => {
        errors.value = {};
        const a = form.value.address || {};
        if (!a.street?.trim() && !a.postalCode?.trim() && !a.city?.trim()) {
          errors.value.address = $t('customer_accounts.sites.validation.address_required');
        }
        return Object.keys(errors.value).length === 0;
      };

      const handleSubmit = async () => {
        if (!validate()) return;
        saving.value = true;
        try {
          const f = form.value;
          await props.onSubmit({
            label: f.label.trim() || null,
            external_ref: f.external_ref.trim() || null,
            address: f.address,
            access_notes: f.access_notes.trim() || null,
            notes: f.notes.trim() || null,
          });
        } finally {
          saving.value = false;
        }
      };

      return { form, errors, saving, isEdit, usage, handleSubmit };
    },
  });
</script>

<style scoped lang="scss">
  .customer-site-form {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    &__row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 0.75rem;

      @include sm-down {
        grid-template-columns: 1fr;
      }
    }

    &__hint {
      display: flex;
      align-items: center;
      gap: 0.375rem;
      margin: 0;
      font-size: 0.8125rem;
      color: var(--slate-500);
    }

    &__actions {
      display: flex;
      justify-content: flex-end;
      gap: 0.5rem;
      padding-top: 0.75rem;
      border-top: 1px solid var(--slate-200);
    }
  }
</style>
